import { useState, useEffect, useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MessageSquare, Send, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function BookingChat({ booking, isOpen, onClose, currentUserType }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isOnline, setIsOnline] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const heartbeatRef = useRef(null);

  useEffect(() => {
    if (isOpen && booking) {
      fetchMessages();
      checkPresence();
      // Poll messages every 3s for live chat
      pollRef.current = setInterval(() => {
        fetchMessages();
        checkPresence();
      }, 3000);
      // If business owner, send heartbeat
      if (currentUserType === "business") {
        sendHeartbeat();
        heartbeatRef.current = setInterval(sendHeartbeat, 30000);
      }
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };
  }, [isOpen, booking]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const checkPresence = async () => {
    if (!booking?.business_id) return;
    try {
      const res = await axios.get(`${API}/presence/${booking.business_id}`);
      setIsOnline(res.data.is_online);
    } catch {}
  };

  const sendHeartbeat = async () => {
    try {
      await axios.post(`${API}/presence/heartbeat`, {}, { withCredentials: true });
    } catch {}
  };

  const fetchMessages = async () => {
    if (!booking) return;
    try {
      const res = await axios.get(
        `${API}/bookings/${booking.booking_id}/messages`,
        { withCredentials: true }
      );
      setMessages(res.data);
    } catch {}
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/bookings/${booking.booking_id}/messages`,
        { content: newMessage },
        { withCredentials: true }
      );
      setMessages((prev) => [...prev, res.data]);
      setNewMessage("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio messaggio");
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const otherName = currentUserType === "business" ? booking?.customer_name : booking?.business_name;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="rounded-3xl border-[rgba(226,114,91,0.15)] bg-white max-w-lg h-[550px] flex flex-col shadow-warm-lg p-0 overflow-hidden" data-testid="booking-chat-dialog">
        {/* Chat Header */}
        <div className="bg-gradient-to-r from-[#E2725B] to-[#F39C12] px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-white" strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-bold text-white text-sm">{otherName || "Chat"}</p>
                <p className="text-white/70 text-xs">{booking?.date} - {booking?.time_slot}</p>
              </div>
            </div>
            {/* Online status */}
            <div className="flex items-center gap-2" data-testid="online-status">
              <span className={`relative flex h-3 w-3`}>
                {isOnline ? (
                  <>
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
                  </>
                ) : (
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
                )}
              </span>
              <span className="text-xs text-white/80 font-medium">
                {isOnline ? "Online" : "Offline"}
              </span>
            </div>
          </div>
        </div>

        {/* Customer Notes */}
        {booking?.customer_notes && (
          <div className="mx-4 mt-3 p-3 bg-amber-50 border border-amber-200 rounded-2xl text-xs">
            <p className="font-bold text-amber-700 mb-1">Note:</p>
            <p className="text-[#2A1B14]">{booking.customer_notes}</p>
          </div>
        )}

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="chat-messages-area">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-[#E2725B]" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="h-10 w-10 text-[#E2725B]/20 mx-auto mb-3" />
              <p className="text-sm text-[#6A564A]">{t("chat.empty")}</p>
            </div>
          ) : (
            messages.map((msg) => {
              const isOwn = msg.sender_type === currentUserType;
              return (
                <div
                  key={msg.message_id}
                  className={`flex ${isOwn ? "justify-end" : "justify-start"}`}
                  data-testid={`chat-message-${msg.message_id}`}
                >
                  <div
                    className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                      isOwn
                        ? "bg-gradient-to-r from-[#E2725B] to-[#F39C12] text-white rounded-br-sm"
                        : "bg-[#FDFBF7] text-[#2A1B14] border border-[rgba(226,114,91,0.1)] rounded-bl-sm"
                    }`}
                  >
                    <p className={`text-xs font-bold mb-1 ${isOwn ? "text-white/80" : "text-[#E2725B]"}`}>
                      {msg.sender_name}
                    </p>
                    <p className="text-sm leading-relaxed">{msg.content}</p>
                    <p className={`text-[10px] mt-1.5 ${isOwn ? "text-white/60" : "text-[#6A564A]"}`}>
                      {new Date(msg.created_at).toLocaleString("it-IT", {
                        day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
                      })}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t border-[rgba(226,114,91,0.1)] p-4 flex gap-2 bg-white">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t("chat.placeholder")}
            className="rounded-full border-[rgba(226,114,91,0.15)] flex-1 px-5 focus:ring-[#E2725B] focus:border-[#E2725B]"
            disabled={sending}
            data-testid="chat-input"
          />
          <Button
            onClick={sendMessage}
            disabled={sending || !newMessage.trim()}
            className="rounded-full bg-gradient-to-r from-[#E2725B] to-[#F39C12] text-white border-0 w-11 h-11 p-0 hover:scale-105 active:scale-95 transition-transform shadow-lg shadow-[#E2725B]/20"
            data-testid="chat-send-btn"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
